import { parseEntityItemId } from '@wix/a11y-rules';
import { Box, Text } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { convertElementStatusToBooleanStatus, RuleElementListHeader, useAutoScrolling, useDeepCompare, useElementToggle } from '@wix/rules-panel-ui';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo } from 'react';
import { currentElementInfoAtom } from '../../../atoms/bi-atoms';
import { scrollerNodeRefAtom } from '../../../atoms/session-atoms';
import { BiCtaNames } from '../../../bi/types';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../hooks/use-bi-logger';
import { getCompRefType } from '../../../utils/get-comp-ref-type';
import { convertBooleanStatusToElementStatus } from '../alt-image-fixer/utils';
import { ColorContrastRule } from './color-contrast-rule';
import { useColorContrastAnalytics } from './hooks/use-color-contrast-analytics';
import { useColorContrastFixer } from './hooks/use-color-contrast-fixer';
import { useGetColorContrastElementsInSession } from './hooks/use-get-color-contrast-elements-in-session';
export const ColorContrastRuleFixer = _ref => {
  let {
    failedElements,
    passedElements,
    pageId,
    breakpointId
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    editorHost
  } = useA11yWizardAdapter();
  const setCurrentElementInfo = useSetAtom(currentElementInfoAtom);
  const {
    collapseElement,
    openNextElement,
    toggleElement,
    selectedElementId
  } = useElementToggle();
  const biLogger = useBiLogger();
  const {
    a11yWizardAdapter
  } = useA11yWizardAdapter();
  const scrollerNodeRef = useAtomValue(scrollerNodeRefAtom);
  const {
    selectedItemContentRef,
    selectedItemHeaderRef
  } = useAutoScrolling(selectedElementId, scrollerNodeRef);
  const {
    elements,
    completedSize
  } = useGetColorContrastElementsInSession({
    failedElements,
    pageId,
    passedElements
  });
  const stableElements = useDeepCompare(elements);
  const memoizedElements = useMemo(() => (stableElements == null ? void 0 : stableElements.map(el => ({
    breakpointId: undefined,
    // TODO: once we support breakpoints, add the breakpointId
    id: el.uiElementId,
    status: convertBooleanStatusToElementStatus(el.isValid)
  }))) ?? [], [stableElements]);
  const {
    onApplyClick,
    showLoader,
    onChangeElementsColor,
    setShowLoader,
    isColorContrastGenericComponentForeground,
    isColorContrastGenericComponentBackground
  } = useColorContrastFixer({
    elements,
    numOfElements: elements.length,
    pageId
  });
  const {
    manualTaskDataEvent
  } = useColorContrastAnalytics({
    elements,
    failedElements,
    pageId
  });
  const handleClickCancel = useCallback(index => () => {
    biLogger.taskElementCtaClick({
      ctaName: BiCtaNames.Cancel,
      index,
      status: memoizedElements[index].status
    });
    collapseElement();
  }, [biLogger, collapseElement, memoizedElements]);
  const handleCollapseAnimationEnd = useCallback(() => {
    setShowLoader(false);
  }, [setShowLoader]);
  const openColorContrastElement = useCallback(_ref2 => {
    let {
      id: uiElementId
    } = _ref2;
    const selectedElementIndex = memoizedElements.findIndex(el => el.id === uiElementId);
    setCurrentElementInfo({
      elementIndex: selectedElementIndex,
      elementStatus: convertElementStatusToBooleanStatus(memoizedElements[selectedElementIndex].status)
    });
    const {
      entities
    } = parseEntityItemId(uiElementId, 1);
    const compId = entities.length > 0 ? entities[0] : '';
    const nextCompRef = {
      id: compId,
      type: getCompRefType(editorHost, breakpointId)
    };
    if (nextCompRef) {
      a11yWizardAdapter.selectComponent(nextCompRef);
    }
  }, [a11yWizardAdapter, breakpointId, editorHost, memoizedElements, setCurrentElementInfo]);
  useEffect(() => {
    manualTaskDataEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    openNextElement(memoizedElements, openColorContrastElement, handleCollapseAnimationEnd);
  }, [handleCollapseAnimationEnd, memoizedElements, openColorContrastElement, openNextElement]);
  const CompletedHeader = () => /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "thin",
    secondary: true
  }, t('accessibility-wizard.rules-list.color-contrast.list.completed.header'));
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP2"
  }, elements.length === 0 ? /*#__PURE__*/React.createElement(CompletedHeader, null) : /*#__PURE__*/React.createElement(RuleElementListHeader, {
    completedSize: completedSize,
    title: t('accessibility-wizard.rules-list.color-contrast.list.failed-elements-number.heading'),
    totalSize: elements.length
  }), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "9px"
  }, elements.map((element, index) => {
    const {
      elementId,
      affectingAncestors,
      uiElementId,
      className
    } = element;
    const onOpenElement = () => {
      const compRef = {
        id: elementId,
        type: getCompRefType(editorHost, breakpointId)
      };
      a11yWizardAdapter.selectComponent(compRef);
    };
    const isGeneric = isColorContrastGenericComponentForeground(elementId, className ?? '') || ((affectingAncestors == null ? void 0 : affectingAncestors.some(_ref3 => {
      let [id,, ancestorClassName] = _ref3;
      return isColorContrastGenericComponentBackground(id, ancestorClassName ?? '');
    })) ?? true);
    return /*#__PURE__*/React.createElement(ColorContrastRule, {
      contentRef: selectedItemContentRef,
      element: element,
      elementIndex: index,
      headerRef: selectedItemHeaderRef,
      isGeneric: isGeneric,
      isOpen: selectedElementId === uiElementId,
      key: uiElementId,
      pageId: pageId,
      showLoader: showLoader,
      toggleElement: toggleElement(uiElementId, onOpenElement),
      onChangeElementsColor: onChangeElementsColor,
      onClickApply: onApplyClick,
      onClickCancel: handleClickCancel(index)
    });
  })));
};
//# sourceMappingURL=color-contrast-rule-fixer.js.map